import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  computed,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime, forkJoin, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { PurchaseOrderService } from '../services/purchase-order.service';
import { VendorService } from '../../vendor/services/vendor.service';
import { VendorOptionsService } from '@shared/services/vendor-options.service';
import { PaginatedDropdownLoadParams } from '@shared/utils/paginated-dropdown.util';
import { VendorSkuControllerService } from '@app/api/vendor-service/api/api';
import { PurchaseOrderConfigService } from '@app/feature/configuration/order/purchase-order-config/services/purchase-order-config.service';
import {
  AddFormComponent,
  FormField,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import {
  applyPurchaseOrderConfigToFields,
  getPurchaseOrderStatusField,
  withModeLabels,
  mapPurchaseOrderItemsToDetailList,
} from '../po.helper';
import {
  PURCHASE_ORDER_ADD_FORM_FIELDS,
  PURCHASE_ORDER_ADD_FORM_FIELDS_PR,
  PURCHASE_ORDER_DETAIL_TABLE_SECTION,
  PURCHASE_ORDER_DETAIL_TABLE_SECTION_NORMAL,
  PURCHASE_ORDER_HEADER_ACTIONS,
  PurchaseOrderAddFormData,
  PurchaseOrderDetailItem,
} from '../purchase-order-add/purchase-order-add.config';
import { PurchaseOrderUpdateDTO } from '@app/api/purchase-service/model/purchaseOrderUpdateDTO';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-purchase-order-edit',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    ButtonModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './purchase-order-edit.component.html',
  styleUrl: './purchase-order-edit.component.css',
})
export class PurchaseOrderEditComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private purchaseOrderService = inject(PurchaseOrderService);
  private vendorService = inject(VendorService);
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private purchaseOrderConfigService = inject(PurchaseOrderConfigService);
  private vendorSkuService = inject(VendorSkuControllerService);
  private readonly vendorOptionsService = inject(VendorOptionsService);
  private destroy$ = new Subject<void>();

  // --- Real-time unit price validation state
  private unitPriceInput$ = new Subject<{ id: string; value: number }>();
  private invalidUnitPriceIds = new Set<string>();
  private unitPriceValidationSubscription: any;

  // Form configuration
  formFields = signal<FormField[]>([]);
  headerActions = signal<ActionButton[]>([]);

  // Data
  detailList = signal<PurchaseOrderDetailItem[]>([]);
  selectedDetails = signal<PurchaseOrderDetailItem[]>([]);
  loading = signal(false);
  currentFormData: any = null;
  purchaseOrderId: string | null = null;
  private skipCanDeactivate = false;
  @ViewChild('addForm') private readonly addFormComponent?: AddFormComponent;

  // Mode detection
  isPurchaseRequestMode = signal(false);
  isNormalMode = signal(false);

  // Vendor options for dropdown
  vendorOptions = signal<any[]>([]);

  // Store series value separately since it's disabled and excluded from form.value
  seriesValue = signal<string>('');

  // Computed table sections that automatically update with data
  tableSections = computed(() => {
    if (this.isNormalMode()) {
      // Normal mode - use table section with actions
      const section = {
        ...PURCHASE_ORDER_DETAIL_TABLE_SECTION_NORMAL,
        data: this.detailList(),
      };

      if (section.columns) {
        section.columns = section.columns.map((col: any) => {
          if (col.prop === 'warehouseCode') {
            return { ...col, type: 'text', editable: false };
          }
          if (col.prop === 'unitPrice') {
            return { ...col, type: 'currency', editable: false };
          }
          return col;
        });
      }

      // Edit mode: only delete actions (no add item - chỉ cho sửa giá, sl, thuế hoặc xoá)
      if (section.actions) {
        section.actions = section.actions
          .filter((action: any) => action.action !== 'addDetail')
          .map((action: any) => {
            if (action.action === 'deleteSelectedDetail') {
              return {
                ...action,
                disabled: this.selectedDetails().length === 0,
              };
            }
            return action;
          });
      }

      return [section];
    } else {
      // Purchase request mode - use table section without actions
      return [
        {
          ...PURCHASE_ORDER_DETAIL_TABLE_SECTION,
          data: this.detailList(),
        },
      ];
    }
  });

  ngOnInit(): void {
    this.initializeForm();
    this.loadPurchaseOrder();

    // Debounced input for unit prices (<100 highlight)
    this.unitPriceValidationSubscription = this.unitPriceInput$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(({ id, value }) => {
        if (typeof value !== 'number' || value < 100) {
          this.invalidUnitPriceIds.add(id);
        } else {
          this.invalidUnitPriceIds.delete(id);
        }
        // UI will update via getDetailFieldError
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.unitPriceValidationSubscription) {
      this.unitPriceValidationSubscription.unsubscribe();
    }
  }

  private loadAndApplyPurchaseOrderConfig(): void {
    const config =
      this.purchaseOrderConfigService.getPurchaseOrderFieldsFromStorage();
    if (config && config.length > 0) {
      this.formFields.set(
        applyPurchaseOrderConfigToFields(config, this.formFields()),
      );
      this.cdr.detectChanges();
      return;
    }
    this.purchaseOrderConfigService
      .getPurchaseOrderFields()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fields) => {
          this.formFields.set(
            applyPurchaseOrderConfigToFields(fields, this.formFields()),
          );
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading purchase order config:', err);
        },
      });
  }

  private initializeForm(): void {
    // Get purchase order ID from route
    this.purchaseOrderId = this.route.snapshot.paramMap.get('id');

    // Initialize with default form fields - will be updated after loading data
    this.formFields.set(PURCHASE_ORDER_ADD_FORM_FIELDS);

    // Set header actions for edit
    this.headerActions.set([
      {
        label: 'common.actions.save',
        action: 'save',
        severity: 'info' as const,
      },
      {
        label: 'common.actions.cancel',
        action: 'cancel',
        outlined: true,
        severity: 'secondary' as const,
      },
    ]);

    // Vendor options loaded after PO data (in loadPurchaseOrder)
  }

  private loadPurchaseOrder(): void {
    if (this.purchaseOrderId) {
      this.loading.set(true);

      // Call the actual API to get purchase order details
      this.purchaseOrderService
        .getPurchaseOrderById(this.purchaseOrderId)
        .subscribe({
          next: (purchaseOrder) => {
            if (purchaseOrder) {
              // Store series value separately since it's disabled
              this.seriesValue.set(purchaseOrder.series || '');

              // Set form data first (using the same approach as purchase request edit)
              this.currentFormData = {
                series: purchaseOrder.series || '',
                vendorCode: purchaseOrder.vendorCode || '', // Already mapped from vendorCode in service
                deliveryDate: this.parseDateToMidnight(
                  purchaseOrder.deliveryDate,
                ),
                description: purchaseOrder.description || '',
                purchaseRequest:
                  (purchaseOrder.purchaseRequest &&
                  typeof purchaseOrder.purchaseRequest === 'object' &&
                  'id' in purchaseOrder.purchaseRequest
                    ? (purchaseOrder.purchaseRequest as { id: string | number })
                        .id
                    : null) || null,
                status: purchaseOrder.status || 'PENDING',
              };

              // Store vendor data for later use in form initialization
              // If vendor data is not available, we'll still use vendorCode in dropdown
              if (purchaseOrder.vendor && purchaseOrder.vendor.vendorName) {
                // Ensure current vendor is added to vendor options
                this.addCurrentVendorToOptions(purchaseOrder.vendor);
              } else if (purchaseOrder.vendorCode) {
                // For old POs without vendor data, add vendorCode as option
                this.addVendorCodeAsOption(purchaseOrder.vendorCode);
              }

              const hasPurchaseRequest = !!(
                purchaseOrder.purchaseRequest &&
                typeof purchaseOrder.purchaseRequest === 'object' &&
                'id' in purchaseOrder.purchaseRequest &&
                (purchaseOrder.purchaseRequest as { id: unknown }).id
              );

              // Detect mode based on purchaseRequest.id field
              if (hasPurchaseRequest) {
                this.isPurchaseRequestMode.set(true);
                this.isNormalMode.set(false);
                this.initializeFormForPurchaseRequestMode(purchaseOrder);
              } else {
                this.isPurchaseRequestMode.set(false);
                this.isNormalMode.set(true);
                this.initializeFormForNormalMode(purchaseOrder);
              }

              // Set detail items from API response
              if (purchaseOrder.items && purchaseOrder.items.length > 0) {
                this.detailList.set(
                  mapPurchaseOrderItemsToDetailList(purchaseOrder.items),
                );

                // Force change detection to ensure table updates
                this.cdr.detectChanges();
              } else {
                this.detailList.set([]);
              }

              // Load vendor options by SKU (vendors đáp ứng ít nhất 1 SKU, label "3/3 SKU")
              this.loadVendorOptions();
            }
            this.loading.set(false);
          },
          error: (error) => {
            console.error('Error loading purchase order:', error);
            this.loading.set(false);
          },
        });
    }
  }

  private initializeFormForPurchaseRequestMode(purchaseOrder: any): void {
    const editStyle =
      'background-color: #f5f5f5; color: #666; cursor: not-allowed;';
    let vendorOptions = this.vendorOptions();
    if (
      purchaseOrder.vendor?.vendorCode &&
      purchaseOrder.vendor?.vendorName &&
      !vendorOptions.some((o) => o.value === purchaseOrder.vendor.vendorCode)
    ) {
      vendorOptions = [
        {
          label: purchaseOrder.vendor.vendorName,
          value: purchaseOrder.vendor.vendorCode,
        },
        ...vendorOptions,
      ];
    } else if (
      purchaseOrder.vendorCode &&
      !vendorOptions.some((o) => o.value === purchaseOrder.vendorCode)
    ) {
      vendorOptions = [
        { label: purchaseOrder.vendorCode, value: purchaseOrder.vendorCode },
        ...vendorOptions,
      ];
    }

    const prModeFormFields = PURCHASE_ORDER_ADD_FORM_FIELDS_PR.map(
      (field: any) => {
        if (field.controlName === 'purchaseRequest') {
          return {
            ...withModeLabels(field, 'edit'),
            type: 'searchableDropdown' as const,
            disabled: true,
            options: [],
          };
        }
        if (field.controlName === 'series') {
          return {
            ...withModeLabels(field, 'edit'),
            disabled: true,
            readonly: true,
            style: editStyle,
          };
        }
        if (field.controlName === 'vendorCode') {
          return {
            ...withModeLabels(field, 'edit'),
            label: 'purchase_order.edit.fields.vendor',
            type: 'searchableDropdown' as const,
            options: vendorOptions,
            paginatedOptions: {
              loadPage: (params: PaginatedDropdownLoadParams) =>
                this.vendorOptionsService.getOptionsPage(params, {
                  valueKey: 'code',
                }),
            },
            showClear: false,
          };
        }
        return withModeLabels(field, 'edit');
      },
    );

    const statusField = getPurchaseOrderStatusField('edit');
    this.formFields.set([statusField, ...prModeFormFields]);

    // Populate purchase request options with current data
    this.populatePurchaseRequestOptions(purchaseOrder);

    // Update vendor options if they're already loaded
    if (this.vendorOptions().length > 0) {
      this.updateVendorOptionsInForm(this.vendorOptions());
    }

    // Apply config after form is fully initialized
    this.loadAndApplyPurchaseOrderConfig();

    // Trigger change detection to ensure form updates
    this.cdr.detectChanges();
  }

  private populatePurchaseRequestOptions(purchaseOrder: any): void {
    if (
      purchaseOrder.purchaseRequest &&
      typeof purchaseOrder.purchaseRequest === 'object' &&
      'id' in purchaseOrder.purchaseRequest
    ) {
      const pr = purchaseOrder.purchaseRequest as {
        id: string | number;
        series?: string;
        title?: string;
      };
      const currentFormFields = this.formFields();
      const updatedFields = currentFormFields.map((field) => {
        if (field.controlName === 'purchaseRequest') {
          return {
            ...field,
            options: [
              {
                label:
                  pr.series && pr.title ? `${pr.series} - ${pr.title}` : '',
                value: pr.id,
              },
            ],
          };
        }
        return field;
      });
      this.formFields.set(updatedFields);
    }
  }

  /**
   * Load vendor options: vendors đáp ứng ít nhất 1 SKU trong đơn, label "Tên NCC (matched/total SKU)".
   * Nếu đơn không có dòng nào thì load tất cả NCC ACTIVE.
   */
  private loadVendorOptions(): void {
    const items = this.detailList();
    const skuCodes = [...new Set(items.map((i) => i.skuCode).filter(Boolean))];
    const totalSkus = skuCodes.length;

    if (totalSkus === 0) {
      this.ensureCurrentVendorInOptions();
      return;
    }

    this.vendorService
      .getVendors({
        page: 0,
        size: 100,
        status: 'ACTIVE',
        skuCodes,
        sortBy: 'vendorName',
        ascending: true,
      })
      .pipe(
        switchMap((vendorResponse) => {
          if (!vendorResponse.data || vendorResponse.data.length === 0) {
            return of({ vendors: [], vendorSkuMap: new Map(), totalSkus });
          }
          const vendorCodes = vendorResponse.data.map((v: any) => v.code);
          const vendorSkuRequests = vendorCodes.map((vendorCode: string) =>
            this.vendorSkuService
              .page1(
                undefined,
                vendorCode,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
                0,
                100,
                'skuCode',
                true,
                'body',
                false,
                { httpHeaderAccept: 'application/json' as any },
              )
              .pipe(
                map((response: any) => ({
                  vendorCode,
                  skuCodes:
                    response.pageContent?.map((sku: any) => sku.skuCode) || [],
                })),
                catchError(() => of({ vendorCode, skuCodes: [] })),
              ),
          );
          return forkJoin(vendorSkuRequests).pipe(
            map((vendorSkuResults) => {
              const vendorSkuMap = new Map<string, number>();
              const skuCodeSet = new Set(skuCodes);
              vendorSkuResults.forEach((result: any) => {
                const matchedCount = result.skuCodes.filter((c: string) =>
                  skuCodeSet.has(c),
                ).length;
                vendorSkuMap.set(result.vendorCode, matchedCount);
              });
              return {
                vendors: vendorResponse.data,
                vendorSkuMap,
                totalSkus,
              };
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ vendors, vendorSkuMap, totalSkus }) => {
          if (!vendors || vendors.length === 0) {
            this.vendorOptions.set([]);
            this.ensureCurrentVendorInOptions();
            this.updateVendorOptionsInForm(this.vendorOptions());
            return;
          }
          const sortedVendors = [...vendors].sort((a: any, b: any) => {
            const countA = vendorSkuMap.get(a.code) || 0;
            const countB = vendorSkuMap.get(b.code) || 0;
            if (countB !== countA) return countB - countA;
            return (a.name || a.code || '').localeCompare(
              b.name || b.code || '',
            );
          });
          const options = sortedVendors.map((vendor: any) => {
            const matchedCount = vendorSkuMap.get(vendor.code) || 0;
            return {
              label: `${vendor.name || vendor.code} (${matchedCount}/${totalSkus} SKU)`,
              value: vendor.code,
            };
          });
          this.vendorOptions.set(options);
          this.ensureCurrentVendorInOptions();
          this.updateVendorOptionsInForm(this.vendorOptions());
        },
        error: (err) => {
          console.error('Error loading vendor options:', err);
          this.vendorOptions.set([]);
          this.updateVendorOptionsInForm(this.vendorOptions());
        },
      });
  }

  private ensureCurrentVendorInOptions(): void {
    const code = this.currentFormData?.vendorCode;
    if (!code) return;
    const opts = this.vendorOptions();
    if (opts.some((o) => o.value === code)) return;
    this.vendorOptions.set([{ label: code, value: code }, ...opts]);
  }

  private updateVendorOptionsInForm(vendorOptions?: any[]): void {
    if (!vendorOptions || vendorOptions.length === 0) {
      // If no options provided, don't update
      return;
    }
    const currentFormFields = this.formFields();
    if (currentFormFields.length === 0) {
      // Form fields not initialized yet, options will be applied when form is initialized
      return;
    }
    const updatedFields = currentFormFields.map((field) => {
      if (field.controlName === 'vendorCode') {
        return {
          ...field,
          options: vendorOptions,
          label: 'purchase_order.edit.fields.vendor', // Ensure correct label
        };
      }
      return field;
    });
    this.formFields.set(updatedFields);
    this.cdr.detectChanges();
  }

  // Add current vendor to options if not already present
  private addCurrentVendorToOptions(vendor: any): void {
    if (vendor && vendor.vendorCode && vendor.vendorName) {
      const currentVendorOption = {
        label: vendor.vendorName,
        value: vendor.vendorCode,
      };
      const currentOptions = this.vendorOptions();
      const vendorExists = currentOptions.some(
        (opt) => opt.value === vendor.vendorCode,
      );
      if (!vendorExists) {
        this.vendorOptions.set([currentVendorOption, ...currentOptions]);
      }
    }
  }

  // Add vendorCode as option when vendor data is not available
  private addVendorCodeAsOption(vendorCode: string): void {
    const currentOptions = this.vendorOptions();
    const vendorExists = currentOptions.some((opt) => opt.value === vendorCode);
    if (!vendorExists) {
      // Use vendorCode as both label and value when vendor name is not available
      const vendorCodeOption = {
        label: vendorCode,
        value: vendorCode,
      };
      this.vendorOptions.set([vendorCodeOption, ...currentOptions]);
    }
  }

  private initializeFormForNormalMode(purchaseOrder: any): void {
    // Create form fields for normal mode (without purchaseRequest field)
    const normalModeFormFields = PURCHASE_ORDER_ADD_FORM_FIELDS.filter(
      (field: any) => field.controlName !== 'purchaseRequest',
    ).map((field: any) => {
      let defaultValue =
        purchaseOrder[field.controlName as keyof typeof purchaseOrder];

      // Convert ISO date string to Date object for date fields
      if (
        field.controlName === 'deliveryDate' &&
        defaultValue &&
        typeof defaultValue === 'string'
      ) {
        defaultValue = new Date(defaultValue);
      }

      // Disable series field as it cannot be updated
      const isDisabled = field.controlName === 'series';

      // For vendorCode field, ensure it's a searchable dropdown with options
      if (field.controlName === 'vendorCode') {
        // Ensure current vendor is in options
        let vendorOptions = this.vendorOptions();
        if (
          purchaseOrder.vendor &&
          purchaseOrder.vendor.vendorCode &&
          purchaseOrder.vendor.vendorName
        ) {
          const currentVendorOption = {
            label: purchaseOrder.vendor.vendorName,
            value: purchaseOrder.vendor.vendorCode,
          };
          // Check if current vendor is already in options
          const vendorExists = vendorOptions.some(
            (opt) => opt.value === purchaseOrder.vendor.vendorCode,
          );
          if (!vendorExists) {
            vendorOptions = [currentVendorOption, ...vendorOptions];
          }
        } else if (purchaseOrder.vendorCode) {
          // For old POs without vendor data, add vendorCode as option
          const vendorCodeOption = {
            label: purchaseOrder.vendorCode,
            value: purchaseOrder.vendorCode,
          };
          const vendorExists = vendorOptions.some(
            (opt) => opt.value === purchaseOrder.vendorCode,
          );
          if (!vendorExists) {
            vendorOptions = [vendorCodeOption, ...vendorOptions];
          }
        }
        return {
          ...withModeLabels(field, 'edit'),
          label: 'purchase_order.edit.fields.vendor',
          type: 'searchableDropdown' as const,
          defaultValue,
          options: vendorOptions,
          showClear: false,
        };
      }

      const editStyle = isDisabled
        ? 'background-color: #f5f5f5; color: #666; cursor: not-allowed;'
        : undefined;
      return {
        ...withModeLabels(field, 'edit'),
        defaultValue,
        disabled: isDisabled,
        readonly: isDisabled,
        style: editStyle,
      };
    });

    const statusField = getPurchaseOrderStatusField(
      'edit',
      purchaseOrder.status,
    );
    this.formFields.set([statusField, ...normalModeFormFields]);

    // Update vendor options if they're already loaded
    if (this.vendorOptions().length > 0) {
      this.updateVendorOptionsInForm(this.vendorOptions());
    }

    // Apply config after form is fully initialized
    this.loadAndApplyPurchaseOrderConfig();

    // Trigger change detection to ensure form updates
    this.cdr.detectChanges();
  }

  // Handle form value changes
  onFormValueChanges(formData: any): void {
    const prevVendor = this.currentFormData?.vendorCode;
    this.currentFormData = {
      ...formData,
      series: this.seriesValue(),
    };
    // Khi đổi NCC: load vendor SKU và cập nhật vendorSkuCode/vendorSkuName trên từng dòng
    if (formData?.vendorCode && formData.vendorCode !== prevVendor) {
      this.loadVendorSkusAndUpdateDetails(formData.vendorCode);
    }
  }

  private loadVendorSkusAndUpdateDetails(vendorCode: string): void {
    this.vendorSkuService
      .page1(
        undefined,
        vendorCode,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
        0,
        100,
        'skuCode',
        true,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const list: Array<{
            skuCode?: string;
            vendorSkuCode?: string;
            vendorProductName?: string;
          }> = response.pageContent || [];
          const bySku = new Map(
            list.map((s: (typeof list)[number]) => [s.skuCode, s] as const),
          );
          const items = this.detailList().map((item) => {
            const vs = bySku.get(item.skuCode) as
              | { vendorSkuCode?: string; vendorProductName?: string }
              | undefined;
            return {
              ...item,
              vendorSkuCode: vs?.vendorSkuCode ?? item.vendorSkuCode ?? '',
              vendorSkuName: vs?.vendorProductName ?? item.vendorSkuName ?? '',
            };
          });
          this.detailList.set(items);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading vendor SKUs:', err);
        },
      });
  }

  // Handle form actions (save, cancel)
  onFormAction(action: string): void {
    switch (action) {
      case 'save':
        this.onSave();
        break;
      case 'cancel':
        this.onCancel();
        break;
    }
  }

  // --- Provide error type for data-table's getFieldError ---
  getDetailFieldError(rowIdx: number, field: string): string | null {
    if (field !== 'unitPrice') return null;
    const row = this.detailList()[rowIdx];
    if (!row) return null;
    // Validation: too low
    if (this.invalidUnitPriceIds.has(row.id)) {
      return this.translateService.instant(
        'purchase_order.add.validation.unit_price_min_tooltip',
      );
    }
    return null;
  }

  // Handle table actions (delete detail items only; edit PO không cho thêm mặt hàng)
  onTableAction(event: { action: string; data?: any; section?: string }): void {
    switch (event.action) {
      case 'deleteDetail':
        this.onDeleteDetail(event.data);
        break;
      case 'deleteSelectedDetail':
        this.onDeleteSelectedDetails();
        break;
      case 'selectionChange':
        this.onSelectionChange(event.section, event.data);
        break;
      case 'cellValueChange':
        this.onCellValueChange(event.data);
        break;
      case 'cellInputChange': {
        // Only track unit price edits
        const { rowData, prop, value } = event.data || {};
        if (prop === 'unitPrice' && rowData && rowData.id) {
          this.unitPriceInput$.next({ id: rowData.id, value });
        }
        break;
      }
    }
  }

  // Delete detail item
  onDeleteDetail(item: PurchaseOrderDetailItem): void {
    const currentItems = this.detailList();
    const updatedItems = currentItems.filter((i) => i.id !== item.id);
    this.detailList.set(updatedItems);

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.success'),
      detail: this.translateService.instant(
        'purchase_order.edit.messages.item_deleted',
      ),
    });
  }

  // Delete selected detail items
  onDeleteSelectedDetails(): void {
    const selectedItems = this.selectedDetails();
    if (selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'purchase_order.edit.messages.no_items_selected',
        ),
      });
      return;
    }

    const currentItems = this.detailList();
    const selectedIds = selectedItems.map((item) => item.id);
    const updatedItems = currentItems.filter(
      (item) => !selectedIds.includes(item.id),
    );

    this.detailList.set(updatedItems);
    this.selectedDetails.set([]);

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.success'),
      detail: this.translateService.instant(
        'purchase_order.edit.messages.items_deleted',
        { count: selectedItems.length },
      ),
    });
  }

  // Handle selection changes for bulk delete
  onSelectionChange(section: string | undefined, selectedItems: any[]): void {
    this.selectedDetails.set(selectedItems);
  }

  // Handle cell value changes for editable fields
  onCellValueChange(event: { rowData: any; prop: string; value: any }): void {
    this.updateItemTotalValue(event.rowData, event.prop, event.value);
  }

  private updateItemTotalValue(
    item: PurchaseOrderDetailItem,
    prop: string,
    value: any,
  ): void {
    const currentItems = this.detailList();
    const updatedItems = currentItems.map((i) => {
      if (i.id === item.id) {
        const updatedItem = { ...i, [prop]: value };

        // Recalculate total value if quantity, unitPrice, or tax changed
        if (prop === 'quantity' || prop === 'unitPrice' || prop === 'tax') {
          const quantity = prop === 'quantity' ? value : updatedItem.quantity;
          const unitPrice =
            prop === 'unitPrice' ? value : updatedItem.unitPrice;
          const tax = prop === 'tax' ? value : updatedItem.tax;

          // Calculate total value: quantity * unitPrice * (1 + tax/100)
          updatedItem.totalValue = quantity * unitPrice * (1 + tax / 100);
        }

        return updatedItem;
      }
      return i;
    });

    this.detailList.set(updatedItems);
  }

  // Save purchase order
  onSave(): void {
    // Save-path validation: block if any item invalid
    if (
      this.detailList().some(
        (item) => typeof item.unitPrice !== 'number' || item.unitPrice < 100,
      )
    ) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant(
          'purchase_order.add.validation.unit_price_min_toast',
        ),
      });
      return;
    }

    if (this.validateForm()) {
      this.loading.set(true);

      const purchaseOrderData = {
        ...this.currentFormData,
        id: this.purchaseOrderId,
        detailItems: this.detailList(),
      };

      // Call actual API to update purchase order
      const updateData: PurchaseOrderUpdateDTO = {
        vendorCode: this.currentFormData.vendorCode, // Use vendorCode directly from form
        deliveryDate: this.currentFormData.deliveryDate,
        description: this.currentFormData.description,
        items: this.detailList().map((item) => {
          // Convert tax from percentage (8) back to decimal (0.08) for API
          // UI stores tax as percentage (8), but API expects decimal (0.08)
          const taxDecimal = item.tax != null ? item.tax / 100 : 0;
          const itemData: any = {
            skuCode: item.skuCode,
            vendorSkuCode: item.vendorSkuCode ?? '',
            price: item.unitPrice,
            tax: taxDecimal, // Convert from percentage to decimal for API
            quantity: item.quantity,
            warehouseCode: item.warehouseCode ?? '',
            deleteFlag: false,
          };

          // Only include id if it's a valid number (existing item)
          const itemId = parseInt(item.id);
          if (itemId && itemId > 0) {
            itemData.id = itemId;
          }

          // Handle purchaseRequestItem based on purchaseRequest
          if (
            this.currentFormData.purchaseRequest &&
            this.currentFormData.purchaseRequest !== ''
          ) {
            // If purchaseRequest exists, use purchaseRequestItem (or null if not set)
            itemData.purchaseRequestItem = item.purchaseRequestItem || null;
          } else {
            // If purchaseRequest is null, set purchaseRequestItem to null
            itemData.purchaseRequestItem = null;
          }

          return itemData;
        }),
      };

      this.purchaseOrderService
        .updatePurchaseOrder(this.purchaseOrderId!, updateData)
        .subscribe({
          next: (response) => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: String(
                this.translateService.instant('common.messages.success'),
              ),
              detail: String(
                this.translateService.instant(
                  'purchase_order.edit.messages.order_updated',
                ),
              ),
              life: 3000,
            });

            // Navigate back to purchase order list after delay to allow toast to display
            setTimeout(() => {
              this.router.navigate(['/procurement/purchase-order']);
            }, 1000);
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error updating purchase order:', error);
            this.handleApiError(error);
          },
        });
    }
  }

  // Handle API errors with specific error message detection
  private handleApiError(error: any): void {
    let errorResponse = error?.error || {};
    let errorString = '';

    // Handle string response - try to parse as JSON first
    if (typeof errorResponse === 'string') {
      errorString = errorResponse;
      try {
        errorResponse = JSON.parse(errorResponse);
      } catch (e) {
        // If parsing fails, use the string as is
        errorResponse = {};
      }
    }

    // Convert errorResponse to string for checking if it's an object
    if (typeof errorResponse === 'object' && errorResponse !== null) {
      errorString = JSON.stringify(errorResponse);
    } else if (typeof errorResponse === 'string') {
      errorString = errorResponse;
    }

    // Check if error string includes "deliveryDate" AND "must be future or present date"
    const lowerErrorString = errorString.toLowerCase();
    if (
      lowerErrorString.includes('deliverydate') &&
      lowerErrorString.includes('must be future or present date')
    ) {
      this.messageService.add({
        severity: 'error',
        summary: String(this.translateService.instant('common.messages.error')),
        detail: String(
          this.translateService.instant(
            'purchase_order.edit.messages.delivery_date_must_be_future_or_present',
          ),
        ),
      });
      return;
    }

    // Also check if errorResponse is an object with deliveryDate property
    if (
      errorResponse &&
      typeof errorResponse === 'object' &&
      errorResponse.deliveryDate &&
      typeof errorResponse.deliveryDate === 'string' &&
      errorResponse.deliveryDate
        .toLowerCase()
        .includes('must be future or present date')
    ) {
      this.messageService.add({
        severity: 'error',
        summary: String(this.translateService.instant('common.messages.error')),
        detail: String(
          this.translateService.instant(
            'purchase_order.edit.messages.delivery_date_must_be_future_or_present',
          ),
        ),
      });
      return;
    }

    // Default error message
    this.messageService.add({
      severity: 'error',
      summary: String(this.translateService.instant('common.messages.error')),
      detail: String(
        this.translateService.instant(
          'purchase_order.edit.messages.failed_to_update',
        ),
      ),
    });
  }

  // Cancel and go back
  onCancel(): void {
    this.router.navigate(['/procurement/purchase-order']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  // Validate form
  private validateForm(): boolean {
    const formData = this.currentFormData;
    const detailItems = this.detailList();

    // Validate main form
    if (!formData?.vendorCode || !formData?.series || !formData?.deliveryDate) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant(
          'purchase_order.edit.validation.main_form_required',
        ),
      });
      return false;
    }

    // Validate detail items
    if (detailItems.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant(
          'purchase_order.edit.validation.detail_items_required',
        ),
      });
      return false;
    }

    return true;
  }

  // Format currency for display
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(value);
  }

  // Calculate total value
  getTotalValue(): number {
    return this.detailList().reduce(
      (total, item) => total + item.totalValue,
      0,
    );
  }

  // Parse date to midnight (same as purchase request edit)
  private parseDateToMidnight(dateString: string): Date {
    if (!dateString) return new Date();

    // Parse the date and set time to midnight (00:00:00) to avoid timezone issues
    const date = new Date(dateString);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
}
